/*
 * 戒导航 · 戒打卡模块（jiedh/checkin）前台 JS 总出口
 *
 * 设计依据：doc/戒打卡功能设计方案.md §18.2。
 *
 * ⚠️ 关键：Flarum 的 Application::bootExtensions() 只读取扩展模块的 `extend` 键，
 * 所以这里必须写 `export { default as extend } from './extend';`，而不是默认导出
 *（同 packages/review/js/src/forum/index.ts）。
 *
 * 说明：本模块前台的「初始化动作」集中在 app.initializers —— 两处导航入口：
 *   ① 论坛顶栏 `HeaderPrimary`（全站可见）；
 *   ② 个人主页侧栏 `UserPage.navItems`（仅本人主页可见，方案 A：指向 /u/:slug/checkin）。
 * 路由 / 模型 / 通知组件都在 extend.ts 静态注册，无需额外初始化。
 */
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import UserPage from 'flarum/forum/components/UserPage';
import LinkButton from 'flarum/common/components/LinkButton';

// 扩展声明：独立页路由 + 前端模型 + 通知组件（见 ./extend.ts）
export { default as extend } from './extend';

/**
 * 前台配置对象（= 后端 `ForumCheckinConfig` 序列化出的 `jiedhCheckinConfig`）。
 * 论坛前台 SPA 读不到 settings 表的值，唯一官方通道就是这个 forum 属性（见该 PHP 类头注释）。
 */
function checkinEnabled() {
  const config = app.forum.attribute('jiedhCheckinConfig') ?? {};
  return config.enabled !== false; // 缺省视为开启（与后端默认值 1 一致）
}

/**
 * 顶栏新增「戒打卡」入口 → 独立页 `/checkin`（年度打卡日历 / 大月历 / 时间线都在这页）。
 *
 * ★ 为什么必须有这个入口：
 *   首页顶栏的「戒打卡」按钮只弹出一个「当月月历」浮层，**年度热力图（年度坚持）**
 *   体量较大、无法塞进 380px 浮层，只能跳独立页查看；此前论坛侧没有任何指向 `/checkin`
 *   的入口，用户只能手输网址 —— 本条即补齐这条动线。
 *
 * 技术要点（与 packages/nav 的「论坛 / 博客」入口写法保持一致）：
 *   ① 扩展点 `extend(HeaderPrimary.prototype, 'items')`：与「首页 / 论坛 / 博客」同排，
 *      窄屏下由核心 OverflowingList 自动收进「···」溢出菜单，无需额外适配；
 *   ② 用官方 LinkButton（Button 子类 + m.route.Link）走 SPA 内部路由，点击无整页刷新；
 *   ③ 优先级 70：低于「首页 100 / 论坛 90 / 博客 80」，因此排在「博客」之后；
 *   ④ `active` 显式拼进类名：Flarum 2.x 的 LinkButton `active` 只写 DOM 属性、不生成类名
 *      （见 packages/nav/js/src/forum/index.ts 的同款说明），需手动拼 `active` 类；
 *   ⑤ 双重守卫：功能总开关（jiedhCheckinConfig.enabled）+ 路由是否已注册（'checkin' in app.routes），
 *      停用扩展或关闭功能时入口自动消失，不报错、不留死链接；
 *   ⑥ 2.x 的 Button 无默认类名，必须手写 'Button Button--link' 才能套用官方按钮样式。
 */
function addCheckinNavButton() {
  extend(HeaderPrimary.prototype, 'items', function (items) {
    // 守卫放在渲染期回调内执行，避免初始化器顺序导致误判（与 packages/nav 的写法一致）
    if (!checkinEnabled()) return;
    if (!('checkin' in app.routes)) return;
    const routeName = String(app.current.get('routeName') ?? '');
    items.add('jiedh-checkin-nav', m(LinkButton, {
      className: 'Button Button--link' + (routeName === 'checkin' ? ' active' : ''),
      icon: 'fas fa-calendar-check',
      href: app.route('checkin')
    }, app.translator.trans('jiedh-checkin.forum.nav_label')), 70);
  });
}

/**
 * 个人主页（`UserPage` = `/u/{username}`）侧栏新增「戒打卡」入口 → 个人主页 tab
 * `/u/{username}/checkin`。
 *
 * ★ 设计归属：方案 A「个人主页内以 tab 显示」（用户裁决 2026-09-30，取代原方案 C）。
 *   原方案 C 指向独立页 `/checkin`，点击后脱离个人主页布局，与「回复 / 讨论 /
 *   设置 / 安全」等菜单项的体验不一致；现改为指向 `/u/{slug}/checkin`，
 *   由 UserCheckinPage（extends UserPage）在个人主页布局内渲染打卡内容。
 *
 * ★ 可见范围（用户裁决 2026-09-30）：**仅本人主页显示**。
 *   `app.session.user !== this.user` 时直接 return —— 他人主页与游客态均不渲染。
 *   因此**不引入任何跨用户语义**：后端三个 Scoper（`where('user_id', actor->id)`）
 *   保持不变，**无需放宽**；UserCheckinPage 内亦有同口径的守卫（非本人 → 回首页）。
 *
 * 技术要点：
 *   ① 扩展点 `extend(UserPage.prototype, 'navItems')`：与核心的「帖子 / 讨论 / 设置 / 安全」
 *      同处一个 ItemList（见 vendor/flarum/core/js/src/forum/components/UserPage.tsx）；
 *   ② **完全对齐核心范式**（关键修复）：核心 navItems 只写
 *      `<LinkButton href icon>{...}</LinkButton>`，**不传 `className`**——
 *      侧栏样式来自 `sideNav.less` 的 `.sideNav .Dropdown-menu > li > a
 *      { padding-left: 0; gap: 15px }`。此前本项多传了 `'Button Button--link'`，
 *      引入 Button 的额外内边距 → 与其它菜单项**视觉错位**；现删除 className 即对齐；
 *   ③ `active` 不再手动拼类名：核心的 LinkButton 自带 `isActive`，`listItems()` 会据此
 *      给 `<li>` 加上 `active` 类（侧栏高亮 `.sideNav ... li.active > a` 依赖它）；
 *   ④ 优先级 80：核心 navItems 为 posts 100 → discussions 90 → separator -90
 *      → settings/security -100；本项 80 落在「讨论」之后、分隔线之前，属「个人内容」组；
 *   ⑤ 双重守卫：功能总开关（`jiedhCheckinConfig.enabled`）+ 路由是否已注册
 *      （`'user.checkin' in app.routes`），停用扩展或关闭功能时入口自动消失，不留死链。
 */
function addCheckinProfileNavItem() {
  extend(UserPage.prototype, 'navItems', function (items) {
    // 守卫放在渲染期回调内执行，避免初始化器顺序导致误判（与顶栏入口写法一致）
    if (!checkinEnabled()) return;
    if (!('user.checkin' in app.routes)) return;
    if (app.session.user !== this.user) return; // 仅本人主页：他人主页 / 游客态不显示

    items.add('jiedh-checkin-profile-nav', m(LinkButton, {
      icon: 'fas fa-calendar-check',
      href: app.route('user.checkin', {
        username: this.user.slug()
      })
    }, app.translator.trans('jiedh-checkin.forum.nav_label')), 80);
  });
}
app.initializers.add('jiedh-checkin', () => {
  // 顶栏入口：把「年度打卡日历」这条动线接上（此前只在 /checkin 可达，前端无入口）
  addCheckinNavButton();

  // 个人主页侧栏入口（方案 A·仅本人可见，指向 /u/:slug/checkin）
  addCheckinProfileNavItem();

  // 其余无需额外初始化动作：
  //   - 独立页 /checkin 由 Extend.Routes().add('checkin', ...) 注册；
  //   - 个人主页 tab /u/:username/checkin 由 Extend.Routes().add('user.checkin', ...) 注册；
  //   - 通知组件由 Extend.Notification().add() 注册，通知面板自动渲染。
  // 数据加载全部放在 CheckinContent 的 oninit（惰性，不拖慢首屏）。
});